import { useState } from "react";
import { Loader2, Download } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  fetchProjectAudio,
  getProjectAudioFileUrl,
  getProjectTranscriptionBundleUrl,
  fetchProjectArtifacts,
  getArtifactAudioUrl,
  getProjectFinalAudioUrl,
  type ProjectSummary,
} from "@/api/client";

type DownloadProjectArchiveDialogProps = {
  project: ProjectSummary | null;
  onClose: () => void;
  onError: (msg: string) => void;
};

export function DownloadProjectArchiveDialog({
  project,
  onClose,
  onError,
}: DownloadProjectArchiveDialogProps) {
  const [selectAudios, setSelectAudios] = useState(false);
  const [selectTranscriptions, setSelectTranscriptions] = useState(true);
  const [selectArtifacts, setSelectArtifacts] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isAllSelected = selectAudios && selectTranscriptions && selectArtifacts;

  const handleToggleAll = (checked: boolean) => {
    setSelectAudios(checked);
    setSelectTranscriptions(checked);
    setSelectArtifacts(checked);
  };

  const handleArchiveDownload = async (projectName: string, options: { audios: boolean; transcriptions: boolean; artifacts: boolean }) => {
    try {
      if (options.audios) {
        const files = await fetchProjectAudio(projectName);
        if (files && files.length > 0) {
          for (const file of files) {
            const url = getProjectAudioFileUrl(projectName, file);
            const a = document.createElement("a");
            a.href = url;
            a.download = file;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            await new Promise((resolve) => setTimeout(resolve, 250));
          }
        }
      }

      if (options.transcriptions) {
        const url = getProjectTranscriptionBundleUrl(projectName);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${projectName}_transcriptions.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      if (options.artifacts) {
        const artifacts = await fetchProjectArtifacts(projectName);
        if (artifacts && artifacts.length > 0) {
          for (const art of artifacts) {
            if (art.has_audio) {
              const url = getArtifactAudioUrl(projectName, art.session_id);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${art.name || art.session_id}.wav`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              await new Promise((resolve) => setTimeout(resolve, 250));
            }
          }
        } else {
          const a = document.createElement("a");
          a.href = getProjectFinalAudioUrl(projectName);
          a.download = `${projectName}_final.wav`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }
    } catch (err) {
      console.error("Error during download:", err);
    }
  };

  const handleDownloadConfirm = async () => {
    if (!project) return;
    setIsDownloading(true);
    try {
      await handleArchiveDownload(project.name, {
        audios: selectAudios,
        transcriptions: selectTranscriptions,
        artifacts: selectArtifacts,
      });
      onClose();
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={!!project} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Télécharger l&apos;archive</DialogTitle>
          <DialogDescription>
            Sélectionnez les éléments du projet <span className="font-semibold text-[#0F172B]">«&nbsp;{project?.name}&nbsp;»</span> à télécharger.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-3">
          <div className="flex items-center gap-3 border-b pb-3">
            <Checkbox
              id="select-all"
              checked={isAllSelected}
              onCheckedChange={(checked) => handleToggleAll(!!checked)}
              className="h-5 w-5 rounded-[6px] border-[#8EA4BD] data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
              aria-label="Tout sélectionner"
            />
            <label htmlFor="select-all" className="text-base font-semibold text-[#0F172B] cursor-pointer select-none">
              Tout sélectionner
            </label>
          </div>

          <div className="flex flex-col gap-3.5 pl-1">
            <div className="flex items-center gap-3">
              <Checkbox
                id="select-audios"
                checked={selectAudios}
                onCheckedChange={(checked) => setSelectAudios(!!checked)}
                className="h-5 w-5 rounded-[6px] border-[#8EA4BD] data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                aria-label="Sources audios originales"
              />
              <label htmlFor="select-audios" className="text-base font-normal text-[#0F172B] cursor-pointer select-none">
                Sources audios originales
              </label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="select-transcriptions"
                checked={selectTranscriptions}
                onCheckedChange={(checked) => setSelectTranscriptions(!!checked)}
                className="h-5 w-5 rounded-[6px] border-[#8EA4BD] data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                aria-label="Transcriptions"
              />
              <label htmlFor="select-transcriptions" className="text-base font-normal text-[#0F172B] cursor-pointer select-none">
                Transcriptions
              </label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="select-artifacts"
                checked={selectArtifacts}
                onCheckedChange={(checked) => setSelectArtifacts(!!checked)}
                className="h-5 w-5 rounded-[6px] border-[#8EA4BD] data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                aria-label="Artefacts"
              />
              <label htmlFor="select-artifacts" className="text-base font-normal text-[#0F172B] cursor-pointer select-none">
                Artefacts
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleDownloadConfirm}
            disabled={isDownloading || (!selectAudios && !selectTranscriptions && !selectArtifacts)}
            className="h-[46px] rounded-[14px] bg-[#007AFF] px-6 text-base font-medium text-white hover:bg-[#0066D6] flex items-center gap-2"
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Télécharger
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
